import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { roleLabel } from "@/lib/roleLabels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { toast, Toaster } from "sonner";
import { CheckCircleIcon, XCircleIcon, SpinnerGapIcon } from "@phosphor-icons/react";

export default function JoinPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ loading: true, invite: null, error: null });
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/invites/verify/${token}`);
        setState({ loading: false, invite: r.data, error: null });
      } catch (e) {
        const msg = formatApiErrorDetail(e?.response?.data?.detail) || "Invite is invalid";
        setState({ loading: false, invite: null, error: msg });
      }
    })();
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (password !== confirm) { setErr("Passwords don't match"); return; }
    if (password.length < 8) { setErr("Password must be at least 8 characters"); return; }
    setSubmitting(true);
    try {
      await api.post(`/invites/accept`, { token, name: name.trim(), password });
      toast.success("Welcome to Indorama");
      if (refresh) await refresh();
      nav("/");
    } catch (e) {
      const msg = formatApiErrorDetail(e?.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      <div className="hidden lg:flex flex-col justify-between p-12 text-white relative overflow-hidden"
           style={{ background: "linear-gradient(135deg, #003B95 0%, #005FB2 55%, #0176D3 100%)" }}>
        <div aria-hidden className="absolute inset-0 opacity-[0.08]"
             style={{ backgroundImage: "linear-gradient(rgba(255,255,255,0.7) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.7) 1px, transparent 1px)", backgroundSize: "40px 40px" }}/>
        <div className="relative z-10 flex items-center gap-2">
          <div className="bg-white rounded-md px-3 py-2">
            <img src="/indorama-logo.png" alt="Indorama" className="h-6 w-auto object-contain" />
          </div>
        </div>
        <div className="relative z-10 max-w-md">
          <div className="trk-uppercase-label text-white/70">You're invited</div>
          <h1 className="font-display text-5xl font-extrabold tracking-tight leading-tight mt-4">
            Welcome to<br />Indorama's<br />Initiative Board.
          </h1>
          <p className="text-white/80 mt-6 leading-relaxed">
            Track every initiative, project and task — with AI-generated status reports and a shared operating console.
          </p>
        </div>
        <div className="relative z-10 text-sm text-white/70">One-click activation · Secure</div>
      </div>

      <div className="flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8 lg:hidden">
            <img src="/indorama-logo.png" alt="Indorama" className="h-6 w-auto object-contain" />
          </div>

          {state.loading && (
            <div className="flex items-center gap-2 text-[#475569]" data-testid="join-loading">
              <SpinnerGapIcon className="animate-spin" size={20} /> Verifying invite…
            </div>
          )}

          {!state.loading && state.error && (
            <div data-testid="join-invalid" className="rounded-md p-6 border border-[#FCA5A5] bg-[#FEE2E2]">
              <div className="flex items-center gap-2 text-[#B91C1C] font-medium mb-1">
                <XCircleIcon size={20} weight="fill" /> Invite unavailable
              </div>
              <p className="text-sm text-[#7F1D1D]">{state.error}</p>
              <Link to="/login" className="inline-block mt-4 text-sm text-[#0176D3] hover:underline">← Back to sign in</Link>
            </div>
          )}

          {!state.loading && state.invite && (
            <>
              <div className="mb-8">
                <div className="trk-uppercase-label mb-2">Accept invitation</div>
                <h2 className="font-display text-3xl font-bold tracking-tight text-[#0F172A]">
                  Set up your account
                </h2>
                <p className="text-sm text-[#475569] mt-2">
                  Invited as <span className="font-medium text-[#0F172A]">{roleLabel(state.invite.role)}</span> by{" "}
                  <span className="font-medium text-[#0F172A]">{state.invite.invited_by_name || "an admin"}</span>
                </p>
                <p className="text-xs text-[#94A3B8] mt-1 font-mono">{state.invite.email}</p>
              </div>

              <form onSubmit={submit} className="space-y-4" data-testid="join-form">
                <div>
                  <Label className="trk-uppercase-label mb-2 block">Your full name</Label>
                  <Input value={name} onChange={(e) => setName(e.target.value)} required
                         data-testid="join-name-input" placeholder="Sandeep Naruka" className="h-11 rounded-md" />
                </div>
                <div>
                  <Label className="trk-uppercase-label mb-2 block">Create password</Label>
                  <PasswordInput value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8}
                         data-testid="join-password-input" className="h-11 rounded-md" autoComplete="new-password" />
                </div>
                <div>
                  <Label className="trk-uppercase-label mb-2 block">Confirm password</Label>
                  <PasswordInput value={confirm} onChange={(e) => setConfirm(e.target.value)} required minLength={8}
                         data-testid="join-confirm-input" className="h-11 rounded-md" autoComplete="new-password" />
                </div>

                {err && (
                  <div className="text-sm rounded-md p-3" data-testid="join-error"
                       style={{ color: "#B91C1C", background: "#FEE2E2", border: "1px solid #FCA5A5" }}>{err}</div>
                )}

                <Button type="submit" disabled={submitting} data-testid="join-submit"
                        className="h-11 rounded-md bg-[#0176D3] hover:bg-[#005FB2] text-white font-medium w-full">
                  {submitting ? "Creating account…" : (<span className="inline-flex items-center gap-2"><CheckCircleIcon size={18} weight="fill" /> Accept & join</span>)}
                </Button>

                <p className="text-xs text-[#94A3B8] pt-2">
                  By joining you agree to Indorama's internal Acceptable Use policy.
                </p>
              </form>
            </>
          )}
        </div>
      </div>

      <Toaster position="top-right" richColors />
    </div>
  );
}
